import { parsedOtSegmentStatuses } from '@/areas/claims/claims-models';
import { parsedOtOrganisationEnums } from '@/areas/profile/models';
import { parsedOtContractEnums } from '@/areas/projects/contracts/contract-models';
import { parsedOtProjectStatusEnums } from '@/areas/projects/project-models';
import { parsedApiOrgUserActionsEnums } from '@/models/user-models';
import {
  AconexDocumentMetadataModel as ApiAconexDocumentMetadataModel,
  AconexMailMetadataModel as ApiAconexMailMetadataModel,
  GetDocumentResponseContractDocumentAssociationModel as ApiGetDocumentResponseContractDocumentAssociationModel,
  GetDocumentResponseDocumentVersionModel as ApiGetDocumentResponseDocumentVersionModel,
  GetDocumentResponseFileModel as ApiGetDocumentResponseFileModel,
  GetDocumentResponseModel as ApiGetDocumentResponseModel,
  GetDocumentResponseOrganisationDocumentAssociationModel as ApiGetDocumentResponseOrganisationDocumentAssociationModel,
  GetDocumentResponseProjectDocumentAssociationModel as ApiGetDocumentResponseProjectDocumentAssociationModel,
  GetDocumentResponseSegmentInstanceDocumentAssociationModel as ApiGetDocumentResponseSegmentInstanceDocumentAssociationModel,
} from '@/services/generated/api';
import { OtUserAction } from '@/types/action-enums';
import { OtDocumentReceivalType, parsedOtDocumentReceivalType } from '@/types/document-receival-type';
import { OtContractStatus, OtOrganisationStatus, OtProjectStatus, OtSegmentStatus } from '@/types/status-enums';
import { ZonelessDate } from '@/types/zoneless-date';
import { parsedOtSegmentProcessTypeEnum, SegmentProcessTypeEnum } from '@/wf-components/models/data-driven-enums';
import { DocumentSource as ApiDocumentSource } from '@/services/generated/api';
import {
  OtSegmentInstanceDocumentVersionAssociationType,
  parsedOtSegmentInstanceDocumentVersionAssociationType,
} from '@/types/segment-instance-document-version-association-type';

export enum DocumentListLevel {
  Global,
  Organisation,
  Project,
  Contract,
  Workflow,
}

export enum DocumentListMode {
  Select,
  Display,
}

export type DocumentsFilter = {
  keyword: string | null;
  dateTo: ZonelessDate | null;
  dateFrom: ZonelessDate | null;
  contentDescriptions: string[];
  // all we need, from a filtering perspective, is the guids
  // the autocomplete needs the autocomplete items, but that's an autocomplete concern, not a "here's the filters" concern
  // I think we'll make the job of turning "this list of autocomplete items" into "this list of guids" the filter's concern
  organisationGids: string[];
  projectGids: string[];
  contractGids: string[];
  includeMyUnassociatedDocuments: boolean;
  // TODO document sources
  documentSource: ApiDocumentSource[];
};
export function getEmptyDocumentsFilter(): DocumentsFilter {
  return {
    keyword: null,
    dateFrom: null,
    dateTo: null,
    organisationGids: [],
    projectGids: [],
    contractGids: [],
    contentDescriptions: [],
    includeMyUnassociatedDocuments: false,
    documentSource: [],
  };
}

// TODO move to areas/documents/models.ts maybe? Maybe not?

export interface IDocumentFile {
  /** The Public Identifier (Gid) of the File */
  gid: string;
  /** The Public Identifier (Gid) of the Association between the File and the DocumentVersion */
  associationGid: string;
  /** Name of the file */
  name: string;
  /** Format of the file *This will most likely be just the file extension (which is part of the file name anyway) */
  format: string;
  /** Size of the file in bytes */
  size: number;
  /** The DateTime the the File was uploaded. */
  createdDateUtc: Date;
  /** The DateTime the association between the File and the DocumentVersion was created. */
  associationCreatedDateUtc: Date;
}

export interface IDocumentDetails {
  /** The unique identifier for the Document */
  gid: string;
  /** Details about all Organisations associated with the Document */
  organisationAssociations: IOrganisationDocumentAssociation[];
  /** Details about all Projects associated with the Document */
  projectAssociations: IProjectDocumentAssociation[];
  /** Details about all Contracts associated with the Document */
  contractAssociations: IContractDocumentAssociation[];
  /** Details about all the Versions of the Document */
  versions: IDocumentVersion[];
  /** Distinct set of Actions the User can perform against the Document */
  availableActions: OtUserAction[];
  /** The DateTime the Document was created in the DB. */
  createdDateUtc: Date;
}
export interface IOrganisationDocumentAssociation {
  /** The Public Identifier (Gid) of the association with the Document */
  gid: string;
  /** The Public Identifier (Gid) of the item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  organisationGid: string;
  /** The Code (or Reference in the case of Contract and Segment Instance) of the associated item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  organisationCode: string;
  /** The Name of the associated item (Organisation, Project, Contract, but not Segment Instance because it doesn't have a Name) associated with the Document */
  organisationName: string | null;
  /** Distinct set of Actions the User can perform against the Document Association */
  availableActions: OtUserAction[];
  /** The DateTime the the Association was created. */
  createdDateUtc: Date;
  /** The Organisation Status of the Organisation associated with the Document */
  status: OtOrganisationStatus;
}
export interface IProjectDocumentAssociation {
  /** The Public Identifier (Gid) of the association with the Document */
  gid: string;
  /** The Public Identifier (Gid) of the item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  projectGid: string;
  /** The Code (or Reference in the case of Contract and Segment Instance) of the associated item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  projectCode: string;
  /** The Name of the associated item (Organisation, Project, Contract, but not Segment Instance because it doesn't have a Name) associated with the Document */
  projectName: string | null;
  /** Distinct set of Actions the User can perform against the Document Association */
  availableActions: OtUserAction[];
  /** The DateTime the the Association was created. */
  createdDateUtc: Date;
  /** The Project Status of the Project associated with the Document */
  status: OtProjectStatus;
}
export interface IContractDocumentAssociation {
  /** The Public Identifier (Gid) of the association with the Document */
  gid: string;
  /** The Public Identifier (Gid) of the item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  contractGid: string;
  /** The Code (or Reference in the case of Contract and Segment Instance) of the associated item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  contractCode: string;
  /** The Name of the associated item (Organisation, Project, Contract, but not Segment Instance because it doesn't have a Name) associated with the Document */
  contractName: string | null;
  /** Distinct set of Actions the User can perform against the Document Association */
  availableActions: OtUserAction[];
  /** The DateTime the the Association was created. */
  createdDateUtc: Date;
  /** The Contract Status of the Contract associated with the Document */
  status: OtContractStatus;
}
export interface ISegmentInstanceDocumentAssociation {
  /** The Public Identifier (Gid) of the association with the Document */
  gid: string;
  /** The Public Identifier (Gid) of the item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  segmentInstanceGid: string;
  /** The Code (or Reference in the case of Contract and Segment Instance) of the associated item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  segmentInstanceCode: string;
  /** Distinct set of Actions the User can perform against the Document Association */
  availableActions: OtUserAction[];
  /** The DateTime the the Association was created. */
  createdDateUtc: Date;
  /** The Segment Instance Status of the Segment Instance associated with the Document Version */
  status: OtSegmentStatus;
  /** The Segment Process Type of the Segment parent of the Segment Instance associated with the Document Version */
  segmentProcessType: SegmentProcessTypeEnum;
  /** The Short Name of the Segment parent of the Segment Instance associated with the Document Version */
  segmentShortName: string;
  /** The Name of the Segment parent of the Segment Instance associated with the Document Version */
  segmentName: string;
  associationType: OtSegmentInstanceDocumentVersionAssociationType;
}

export interface IDocumentVersion {
  /** Public identifier of the Document Version */
  gid: string;
  /** The Index of the Version. A number that can be used to create the text "Version 4" */
  index: number;
  /** Document Name */
  name: string;
  /** Document Reference */
  reference: string;
  /** Description of the content of the document */
  contentDescription: string | null;
  /** Description of the document */
  description: string | null;
  /** The date of the Document Version */
  date: ZonelessDate | null;
  /** Now called Receipt Type in the UI */
  receivalType: OtDocumentReceivalType | null;
  /** Only defined if Receival Type is set to 'other' */
  receivalDescription: string | null;
  /** The date and time the document was received in Utc format. */
  receivedUtc: Date | null;
  /** Time zone 'Received Date Time' was supplied to the UI with. Required if 'Received Date Time' is set */
  receivedTimeZone: string | null;
  /** The date and time the document was received in Utc format. */
  deemedReceivedUtc: Date | null;
  /** Time zone 'Deemed Received Date Time' was supplied with. Required if 'Deemed Received Date Time' is set */
  deemedReceivedTimeZone: string | null;
  /** Name of the person that sent the document */
  senderPerson: string | null;
  /** Name of the organisation that sent the document */
  senderOrganisation: string | null;
  /** Name of the person that received the document */
  recipientPerson: string | null;
  /** Name of the organisation that received the document */
  recipientOrganisation: string | null;
  /** Details about all Organisations associated with the Document */
  segmentInstanceAssociations: ISegmentInstanceDocumentAssociation[];
  /** Details about the files associated with this version of the Document */
  files: IDocumentFile[];
  /** Distinct set of Actions the User can perform against the DocumentVersion */
  availableActions: OtUserAction[];
  /** The DateTime the DocumentVersion was created. */
  createdDateUtc: Date;
  // HACK Dan is sick of the left hand right hand stuff. Just going to expose the metadata models directly
  /** Aconex Document Metadata will only be present if the Document was originally created from an Aconex Document */
  aconexDocumentMetadata: ApiAconexDocumentMetadataModel | null;
  /** Other versions of the Aconex Document Metadata. Will only be present if 'Aconex Document Metadata' is present. (We might end up using a cutdown model here because we probably don't need all the Aconex Metadata for all the Versions of the Aconex Document.
  ) */
  aconexDocumentVersions: ApiAconexDocumentMetadataModel[] | null;
  hasMoreRecentAconexDocumentVersion: boolean;
  /** Aconex Mail Metadata will only be present if the Document was originally created from an Aconex Mail */
  aconexMailMetadata: ApiAconexMailMetadataModel | null;
}

export class DocumentFile implements IDocumentFile {
  /** The Public Identifier (Gid) of the File */
  gid!: string;
  /** The Public Identifier (Gid) of the Association between the File and the DocumentVersion */
  associationGid!: string;
  /** Name of the file */
  name!: string;
  /** Format of the file *This will most likely be just the file extension (which is part of the file name anyway) */
  format!: string;
  /** Size of the file in bytes */
  size!: number;
  /** The DateTime the the File was uploaded. */
  createdDateUtc!: Date;
  /** The DateTime the association between the File and the DocumentVersion was created. */
  associationCreatedDateUtc!: Date;

  constructor(data?: IDocumentFile) {
    Object.assign(this, data);
  }

  static createFromApiResponse(data: ApiGetDocumentResponseFileModel): DocumentFile {
    const result = new DocumentFile({
      gid: data.gid,
      associationGid: data.associationGid,
      name: data.name,
      format: data.format,
      size: data.size,
      createdDateUtc: data.createdDateUtc,
      associationCreatedDateUtc: data.associationCreatedDateUtc,
    });
    return result;
  }
}

export class OrganisationDocumentAssociation implements IOrganisationDocumentAssociation {
  /** The Public Identifier (Gid) of the association with the Document */
  gid!: string;
  /** The Public Identifier (Gid) of the item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  organisationGid!: string;
  /** The Code (or Reference in the case of Contract and Segment Instance) of the associated item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  organisationCode!: string;
  /** The Name of the associated item (Organisation, Project, Contract, but not Segment Instance because it doesn't have a Name) associated with the Document */
  organisationName!: string | null;
  /** Distinct set of Actions the User can perform against the Document Association */
  availableActions!: OtUserAction[];
  /** The DateTime the the Association was created. */
  createdDateUtc!: Date;
  /** The Organisation Status of the Organisation associated with the Document */
  status!: OtOrganisationStatus;

  constructor(data: IOrganisationDocumentAssociation) {
    Object.assign(this, data);
  }

  static createFromApiResponse(
    data: ApiGetDocumentResponseOrganisationDocumentAssociationModel,
  ): OrganisationDocumentAssociation {
    const result = new OrganisationDocumentAssociation({
      gid: data.gid,
      organisationGid: data.associatedGid,
      organisationCode: data.associatedCode,
      organisationName: data.associatedName ?? null,
      availableActions: data.availableActions.map(action => parsedApiOrgUserActionsEnums[action]),
      createdDateUtc: data.createdDateUtc,
      status: parsedOtOrganisationEnums[data.status],
    });
    return result;
  }
}

export class ProjectDocumentAssociation implements IProjectDocumentAssociation {
  /** The Public Identifier (Gid) of the association with the Document */
  gid!: string;
  /** The Public Identifier (Gid) of the item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  projectGid!: string;
  /** The Code (or Reference in the case of Contract and Segment Instance) of the associated item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  projectCode!: string;
  /** The Name of the associated item (Organisation, Project, Contract, but not Segment Instance because it doesn't have a Name) associated with the Document */
  projectName!: string | null;
  /** Distinct set of Actions the User can perform against the Document Association */
  availableActions!: OtUserAction[];
  /** The DateTime the the Association was created. */
  createdDateUtc!: Date;
  /** The Project Status of the Project associated with the Document */
  status!: OtProjectStatus;

  constructor(data: IProjectDocumentAssociation) {
    Object.assign(this, data);
  }

  static createFromApiResponse(
    data: ApiGetDocumentResponseProjectDocumentAssociationModel,
  ): ProjectDocumentAssociation {
    const result = new ProjectDocumentAssociation({
      gid: data.gid,
      projectGid: data.associatedGid,
      projectCode: data.associatedCode,
      projectName: data.associatedName ?? null,
      availableActions: data.availableActions.map(action => parsedApiOrgUserActionsEnums[action]),
      createdDateUtc: data.createdDateUtc,
      status: parsedOtProjectStatusEnums[data.status],
    });
    return result;
  }
}

export class ContractDocumentAssociation implements IContractDocumentAssociation {
  /** The Public Identifier (Gid) of the association with the Document */
  gid!: string;
  /** The Public Identifier (Gid) of the item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  contractGid!: string;
  /** The Code (or Reference in the case of Contract and Segment Instance) of the associated item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  contractCode!: string;
  /** The Name of the associated item (Organisation, Project, Contract, but not Segment Instance because it doesn't have a Name) associated with the Document */
  contractName!: string | null;
  /** Distinct set of Actions the User can perform against the Document Association */
  availableActions!: OtUserAction[];
  /** The DateTime the the Association was created. */
  createdDateUtc!: Date;
  /** The Contract Status of the Contract associated with the Document */
  status!: OtContractStatus;

  constructor(data: IContractDocumentAssociation) {
    Object.assign(this, data);
  }

  static createFromApiResponse(
    data: ApiGetDocumentResponseContractDocumentAssociationModel,
  ): ContractDocumentAssociation {
    const result = new ContractDocumentAssociation({
      gid: data.gid,
      contractGid: data.associatedGid,
      contractCode: data.associatedCode,
      contractName: data.associatedName ?? null,
      availableActions: data.availableActions.map(action => parsedApiOrgUserActionsEnums[action]),
      createdDateUtc: data.createdDateUtc,
      status: parsedOtContractEnums[data.status],
    });
    return result;
  }
}

export class SegmentInstanceDocumentAssociation implements ISegmentInstanceDocumentAssociation {
  /** The Public Identifier (Gid) of the association with the Document */
  gid!: string;
  /** The Public Identifier (Gid) of the item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  segmentInstanceGid!: string;
  /** The Code (or Reference in the case of Contract and Segment Instance) of the associated item (Organisation, Project, Contract, Segment Instance) associated with the Document */
  segmentInstanceCode!: string;
  /** Distinct set of Actions the User can perform against the Document Association */
  availableActions!: OtUserAction[];
  /** The DateTime the the Association was created. */
  createdDateUtc!: Date;
  /** The Segment Instance Status of the Segment Instance associated with the Document Version */
  status!: OtSegmentStatus;
  /** The Segment Process Type of the Segment parent of the Segment Instance associated with the Document Version */
  segmentProcessType!: SegmentProcessTypeEnum;
  /** The Short Name of the Segment parent of the Segment Instance associated with the Document Version */
  segmentShortName!: string;
  /** The Name of the Segment parent of the Segment Instance associated with the Document Version */
  segmentName!: string;
  public associationType!: OtSegmentInstanceDocumentVersionAssociationType;

  constructor(data?: ISegmentInstanceDocumentAssociation) {
    Object.assign(this, data);
  }

  static createFromApiResponse(
    data: ApiGetDocumentResponseSegmentInstanceDocumentAssociationModel,
  ): SegmentInstanceDocumentAssociation {
    const result = new SegmentInstanceDocumentAssociation({
      gid: data.gid,
      segmentInstanceGid: data.associatedGid,
      segmentInstanceCode: data.associatedCode,
      availableActions: data.availableActions.map(action => parsedApiOrgUserActionsEnums[action]),
      createdDateUtc: data.createdDateUtc,
      status: parsedOtSegmentStatuses[data.status],
      segmentProcessType: parsedOtSegmentProcessTypeEnum[data.segmentProcessType],
      segmentShortName: data.segmentShortName,
      segmentName: data.segmentName,
      associationType: parsedOtSegmentInstanceDocumentVersionAssociationType[data.associationType],
    });
    return result;
  }
}

export class DocumentVersion implements IDocumentVersion {
  /** Public identifier of the Document Version */
  gid!: string;
  /** The Index of the Version. A number that can be used to create the text "Version 4" */
  index!: number;
  /** Document Name */
  name!: string;
  /** Document Reference */
  reference!: string;
  /** Description of the content of the document */
  contentDescription!: string | null;
  /** Description of the document */
  description!: string | null;
  /** The date of the Document Version */
  date!: ZonelessDate | null;
  /** Now called Receipt Type in the UI */
  receivalType!: OtDocumentReceivalType | null;
  /** Only defined if Receival Type is set to 'other' */
  receivalDescription!: string | null;
  /** The date and time the document was received in Utc format. */
  receivedUtc!: Date | null;
  /** Time zone 'Received Date Time' was supplied to the UI with. Required if 'Received Date Time' is set */
  receivedTimeZone!: string | null;
  /** The date and time the document was received in Utc format. */
  deemedReceivedUtc!: Date | null;
  /** Time zone 'Deemed Received Date Time' was supplied with. Required if 'Deemed Received Date Time' is set */
  deemedReceivedTimeZone!: string | null;
  /** Name of the person that sent the document */
  senderPerson!: string | null;
  /** Name of the organisation that sent the document */
  senderOrganisation!: string | null;
  /** Name of the person that received the document */
  recipientPerson!: string | null;
  /** Name of the organisation that received the document */
  recipientOrganisation!: string | null;
  /** Details about all Organisations associated with the Document */
  segmentInstanceAssociations!: SegmentInstanceDocumentAssociation[];
  /** Details about the files associated with this version of the Document */
  files!: DocumentFile[];
  /** Distinct set of Actions the User can perform against the DocumentVersion */
  availableActions!: OtUserAction[];
  /** The DateTime the DocumentVersion was created. */
  createdDateUtc!: Date;
  /** Aconex Document Metadata will only be present if the Document was originally created from an Aconex Document */
  aconexDocumentMetadata!: ApiAconexDocumentMetadataModel | null;
  /** Other versions of the Aconex Document Metadata. Will only be present if 'Aconex Document Metadata' is present. (We might end up using a cutdown model here because we probably don't need all the Aconex Metadata for all the Versions of the Aconex Document.
  ) */
  aconexDocumentVersions!: ApiAconexDocumentMetadataModel[] | null;
  hasMoreRecentAconexDocumentVersion = false;
  /** Aconex Mail Metadata will only be present if the Document was originally created from an Aconex Mail */
  aconexMailMetadata!: ApiAconexMailMetadataModel | null;

  constructor(data: IDocumentVersion) {
    Object.assign(this, data);
  }

  static createFromApiResponse(data: ApiGetDocumentResponseDocumentVersionModel): DocumentVersion {
    const result = new DocumentVersion({
      gid: data.gid,
      index: data.index,
      name: data.name,
      reference: data.reference,
      contentDescription: data.contentDescription ?? null,
      description: data.description ?? null,
      date: data.date ? new ZonelessDate(data.date) : null,
      receivalType: data.receivalType ? parsedOtDocumentReceivalType[data.receivalType] : null,
      receivalDescription: data.receivalDescription ?? null,
      receivedUtc: data.receivedUtc ?? null,
      receivedTimeZone: data.receivedTimeZone ?? null,
      deemedReceivedUtc: data.deemedReceivedUtc ?? null,
      deemedReceivedTimeZone: data.deemedReceivedTimeZone ?? null,
      senderPerson: data.senderPerson ?? null,
      senderOrganisation: data.senderOrganisation ?? null,
      recipientPerson: data.recipientPerson ?? null,
      recipientOrganisation: data.recipientOrganisation ?? null,
      segmentInstanceAssociations: data.segmentInstanceAssociations.map(
        SegmentInstanceDocumentAssociation.createFromApiResponse,
      ),
      files: data.files.map(DocumentFile.createFromApiResponse),
      availableActions: data.availableActions.map(action => parsedApiOrgUserActionsEnums[action]),
      createdDateUtc: data.createdDateUtc,
      aconexDocumentMetadata: data.aconexDocumentMetadata ?? null,
      aconexDocumentVersions: data.aconexDocumentVersions ?? null,
      hasMoreRecentAconexDocumentVersion: data.hasMoreRecentAconexDocumentVersion ?? false,
      aconexMailMetadata: data.aconexMailMetadata ?? null,
    });
    return result;
  }
}
export class DocumentDetails implements IDocumentDetails {
  /** The unique identifier for the Document */
  gid!: string;
  /** Details about all Organisations associated with the Document */
  organisationAssociations!: OrganisationDocumentAssociation[];
  /** Details about all Projects associated with the Document */
  projectAssociations!: ProjectDocumentAssociation[];
  /** Details about all Contracts associated with the Document */
  contractAssociations!: ContractDocumentAssociation[];
  /** Details about all the Versions of the Document */
  versions!: DocumentVersion[];
  /** Distinct set of Actions the User can perform against the Document */
  availableActions!: OtUserAction[];
  /** The DateTime the Document was created in the DB. */
  createdDateUtc!: Date;

  constructor(data: IDocumentDetails) {
    Object.assign(this, data);
  }

  static createFromApiResponse(data: ApiGetDocumentResponseModel): DocumentDetails {
    const result = new DocumentDetails({
      gid: data.gid,
      organisationAssociations: data.organisationAssociations.map(
        OrganisationDocumentAssociation.createFromApiResponse,
      ),
      projectAssociations: data.projectAssociations.map(ProjectDocumentAssociation.createFromApiResponse),
      contractAssociations: data.contractAssociations.map(ContractDocumentAssociation.createFromApiResponse),
      versions: data.versions
        .map(DocumentVersion.createFromApiResponse)
        .sort((a, b) => b.createdDateUtc.getTime() - a.createdDateUtc.getTime()),
      availableActions: data.availableActions.map(action => parsedApiOrgUserActionsEnums[action]),
      createdDateUtc: data.createdDateUtc,
    });
    return result;
  }
}
